/**
 * Authentication Context
 * Manages user authentication state, tokens, and auth operations
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ACTIVE_ORG_KEY, getActiveOrg } from '../../api/core.js';
import { jwtDecode } from 'jwt-decode';
import { getSubdomainFromHost } from '../utils/tenant';
import { accordionStateAPI } from '../../api.js';
import { migrateTokensIfNeeded } from '../../utils/tokenMigration';

// API base URL — always relative (works behind any reverse proxy)
const API_BASE = process.env.REACT_APP_API_URL || '/api/v1';

// Token storage keys
const ACCESS_TOKEN_KEY = 'fm_access_token';
const REFRESH_TOKEN_KEY = 'fm_refresh_token';

// Create context
const AuthContext = createContext(null);

/**
 * AuthProvider Component
 * Wraps the application and provides authentication state and methods
 */
export function AuthProvider({ children }) {
  const queryClient = useQueryClient();
  const [user, setUser] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [features, setFeatures] = useState({}); // ADR-011 : feature-flags du tenant (/auth/me), confort d'affichage seulement
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isOffboarded, setIsOffboarded] = useState(false);

  // Track impersonation state (check localStorage only once on mount, then track in state)
  const [isImpersonating, setIsImpersonating] = useState(() => {
    return !!localStorage.getItem('fm_impersonate_token');
  });

  // Get subdomain for tenant identification (memoize to prevent recalculation on every render)
  const [subdomain] = useState(() => getSubdomainFromHost());

  /**
   * Store tokens in localStorage
   */
  const storeTokens = useCallback((access_token, refresh_token) => {
    if (access_token) localStorage.setItem(ACCESS_TOKEN_KEY, access_token);
    if (refresh_token) localStorage.setItem(REFRESH_TOKEN_KEY, refresh_token);
  }, []);

  /**
   * Clear tokens from localStorage
   */
  // Group / multi-org: active organization (null = all). Declared here so that
  // clearTokens (logout, auth-failure, refresh failure) can reset it with the session.
  const [activeOrg, setActiveOrgState] = useState(() => getActiveOrg());

  const clearTokens = useCallback(() => {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(ACTIVE_ORG_KEY);
    setActiveOrgState(null);
  }, []);

  /**
   * Get stored access token
   */
  const getAccessToken = useCallback(() => {
    return localStorage.getItem(ACCESS_TOKEN_KEY);
  }, []);

  /**
   * Get stored refresh token
   */
  const getRefreshToken = useCallback(() => {
    return localStorage.getItem(REFRESH_TOKEN_KEY);
  }, []);

  /**
   * Make authenticated API request
   */
  const authFetch = useCallback(async (endpoint, options = {}) => {
    const token = getAccessToken();
    const headers = {
      'Content-Type': 'application/json',
      ...options.headers
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    // Add subdomain header for development
    if (subdomain) {
      headers['X-Tenant-Subdomain'] = subdomain;
    }

    // Group / multi-org: same scope as core.js getAuthHeaders()
    const activeOrgCode = getActiveOrg();
    if (activeOrgCode) {
      headers['X-Active-Org'] = activeOrgCode;
    }

    const response = await fetch(`${API_BASE}${endpoint}`, {
      ...options,
      headers
    });

    // Handle 401 - try to refresh token
    if (response.status === 401) {
      const refreshed = await refreshAuth();
      if (refreshed) {
        // Retry with new token
        headers['Authorization'] = `Bearer ${getAccessToken()}`;
        return fetch(`${API_BASE}${endpoint}`, { ...options, headers });
      }
    }

    return response;
  }, [getAccessToken, subdomain]);

  /**
   * Login with email and password
   */
  const login = useCallback(async (email, password) => {
    setIsLoading(true);
    setError(null);

    try {
      const headers = {
        'Content-Type': 'application/json'
      };

      // Add subdomain header for tenant identification
      if (subdomain) {
        headers['X-Tenant-Subdomain'] = subdomain;
      }

      const response = await fetch(`${API_BASE}/auth/login`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ email, password })
      });

      const data = await response.json();

      if (!data.ok) {
        throw new Error(data.error || 'Login failed');
      }

      // Store tokens
      storeTokens(data.data.access_token, data.data.refresh_token);

      // Decode JWT to check for offboarded status
      try {
        const decoded = jwtDecode(data.data.access_token);
        setIsOffboarded(decoded.isOffboarded === true);
      } catch (e) {
        console.error('Failed to decode JWT:', e);
        setIsOffboarded(false);
      }

      // Set state
      setUser(data.data.user);
      setTenant(data.data.tenant);
      setIsAuthenticated(true);
      setError(null);

      return { success: true, user: data.data.user };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setIsLoading(false);
    }
  }, [storeTokens, subdomain]);

  /**
   * Logout - revoke tokens, reset UI state, and clear auth state
   */
  const logout = useCallback(async () => {
    try {
      // Reset accordion states to all closed
      await accordionStateAPI.reset().catch(() => {});

      const refresh_token = getRefreshToken();
      if (refresh_token) {
        await fetch(`${API_BASE}/auth/logout`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refresh_token })
        });
      }
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      clearTokens();
      // H1 fix (2026-09-13): 'fm_profile' used to survive logout. A driver logging
      // in right after an admin, in the same tab, could inherit the admin's last
      // profile if the /ui_profiles call ever failed (ProfileContext's fallback
      // path trusted this key over the new user's role — see applyFallback fix).
      localStorage.removeItem('fm_profile');
      // Same reasoning for cached query data (vehicles/drivers/claims/... — some
      // with staleTime up to 60s, e.g. UnifiedSearchBar): wipe it so the next
      // session never renders another user's cached rows before its own fetch lands.
      queryClient.clear();
      setUser(null);
      setTenant(null);
      setIsAuthenticated(false);
      setIsOffboarded(false);
    }
  }, [getRefreshToken, clearTokens, queryClient]);

  /**
   * Refresh access token using refresh token
   */
  const refreshAuth = useCallback(async () => {
    const refresh_token = getRefreshToken();
    if (!refresh_token) return false;

    try {
      const response = await fetch(`${API_BASE}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token })
      });

      const data = await response.json();

      if (!data.ok) {
        clearTokens();
        setUser(null);
        setTenant(null);
        setIsAuthenticated(false);
        return false;
      }

      storeTokens(data.data.access_token, data.data.refresh_token);
      return true;
    } catch (err) {
      console.error('Token refresh error:', err);
      clearTokens();
      setUser(null);
      setTenant(null);
      setIsAuthenticated(false);
      return false;
    }
  }, [getRefreshToken, storeTokens, clearTokens]);

  /**
   * Get current user from stored token
   */
  const fetchCurrentUser = useCallback(async () => {
    const token = getAccessToken();
    if (!token) {
      setIsLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_BASE}/auth/me`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (data.ok) {
        // Decode JWT to check for offboarded status
        try {
          const decoded = jwtDecode(token);
          setIsOffboarded(decoded.isOffboarded === true);
        } catch (e) {
          console.error('Failed to decode JWT:', e);
          setIsOffboarded(false);
        }

        setUser(data.data.user);
        setTenant(data.data.tenant);
        setFeatures(data.data.features || {});
        setIsAuthenticated(true);
      } else {
        // Token invalid, try refresh
        const refreshed = await refreshAuth();
        if (refreshed) {
          // Retry
          const retryResponse = await fetch(`${API_BASE}/auth/me`, {
            headers: {
              'Authorization': `Bearer ${getAccessToken()}`,
              'Content-Type': 'application/json'
            }
          });
          const retryData = await retryResponse.json();
          if (retryData.ok) {
            setUser(retryData.data.user);
            setTenant(retryData.data.tenant);
            setFeatures(retryData.data.features || {});
            setIsAuthenticated(true);
          }
        }
      }
    } catch (err) {
      console.error('Fetch user error:', err);
    } finally {
      setIsLoading(false);
    }
  }, [getAccessToken, refreshAuth]);

  /**
   * Change password
   */
  const changePassword = useCallback(async (oldPassword, newPassword) => {
    try {
      const response = await authFetch('/auth/change-password', {
        method: 'POST',
        body: JSON.stringify({ oldPassword, newPassword })
      });

      const data = await response.json();

      if (!data.ok) {
        throw new Error(data.error || 'Password change failed');
      }

      // Force re-login after password change
      await logout();

      return { success: true };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, [authFetch, logout]);

  /**
   * Impersonate a user (superuser only)
   */
  const impersonate = useCallback(async (userId) => {
    if (!user?.isSuperuser && user?.is_superuser !== 1 && user?.is_superuser !== true) {
      return { success: false, error: 'Not authorized' };
    }

    try {
      const response = await authFetch(`/superadmin/impersonate/${userId}`, {
        method: 'POST'
      });

      const data = await response.json();

      if (!data.ok) {
        throw new Error(data.error || 'Impersonation failed');
      }

      // Store impersonation token (keep original tokens for "stop impersonation")
      localStorage.setItem('fm_impersonate_token', data.data.access_token);
      localStorage.setItem('fm_original_token', getAccessToken());

      // Update state
      storeTokens(data.data.access_token, null);
      setUser(data.data.user);
      setTenant(data.data.tenant);
      setIsImpersonating(true); // ← Set state instead of checking localStorage on every render

      return { success: true, user: data.data.user };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, [user, authFetch, getAccessToken, storeTokens]);

  /**
   * Stop impersonation and return to original user
   */
  const stopImpersonation = useCallback(() => {
    const originalToken = localStorage.getItem('fm_original_token');
    if (originalToken) {
      localStorage.setItem(ACCESS_TOKEN_KEY, originalToken);
      localStorage.removeItem('fm_impersonate_token');
      localStorage.removeItem('fm_original_token');
      setIsImpersonating(false); // ← Set state instead of checking localStorage on every render
      fetchCurrentUser();
    }
  }, [fetchCurrentUser]);

  // Migrate tokens to snake_case format on first mount
  useEffect(() => {
    const migrated = migrateTokensIfNeeded();
    if (migrated) {
      // Tokens were cleared, user needs to log in again
      setIsLoading(false);
      setIsAuthenticated(false);
      setUser(null);
      setTenant(null);
    }
  }, []); // Run only once on mount

  // Check for impersonation token from cockpit URL hash (cross-origin handoff)
  useEffect(() => {
    const hash = window.location.hash;
    if (hash.includes('impersonate=')) {
      const params = new URLSearchParams(hash.substring(1));
      const token = params.get('impersonate');
      const refresh = params.get('refresh');
      if (token) {
        storeTokens(token, refresh || null);
        // Clean up URL hash so token is not visible in address bar
        window.history.replaceState(null, '', window.location.pathname);
      }
    }
  }, [storeTokens]);

  // Check for stored token on mount
  useEffect(() => {
    fetchCurrentUser();
  }, [fetchCurrentUser]);

  // Expose token getter + silent refresh for api.js (core.js reads these from window)
  useEffect(() => {
    window.__getAuthToken = getAccessToken;
    window.__getAuthSubdomain = () => subdomain;
    window.__refreshAuth = refreshAuth;
    return () => {
      delete window.__getAuthToken;
      delete window.__getAuthSubdomain;
      delete window.__refreshAuth;
    };
  }, [getAccessToken, subdomain, refreshAuth]);

  // Listen for auth-failure events from core.js (401/403) → force logout
  useEffect(() => {
    const handler = () => {
      if (isAuthenticated) {
        console.log('[Auth] Auth failure detected — forcing logout');
        clearTokens();
        setIsAuthenticated(false);
        setUser(null);
        setTenant(null);
      }
    };
    window.addEventListener('auth-failure', handler);
    return () => window.removeEventListener('auth-failure', handler);
  }, [isAuthenticated, clearTokens]);

  // ── Group / multi-org ─────────────────────────────────────────────────────
  // accessibleOrgs: codes the JWT allows (orgIds). activeOrg: the one chosen in
  // the header switcher, null = all. Persisted in localStorage, sent as X-Active-Org.
  const accessibleOrgs = useMemo(() => {
    if (Array.isArray(user?.accessible_org_codes) && user.accessible_org_codes.length) return user.accessible_org_codes;
    try {
      const token = getAccessToken();
      if (!token) return [];
      const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
      if (Array.isArray(payload.orgIds) && payload.orgIds.length) return payload.orgIds;
      return payload.orgId ? [payload.orgId] : [];
    } catch { return []; }
  }, [user, getAccessToken]);
  const setActiveOrg = useCallback((code) => {
    const next = code && code !== 'all' && accessibleOrgs.includes(code) ? code : null;
    if (next) localStorage.setItem(ACTIVE_ORG_KEY, next); else localStorage.removeItem(ACTIVE_ORG_KEY);
    setActiveOrgState(next);
    // Un changement de périmètre touche toutes les sources (react-query, effets manuels,
    // stores) : on recharge la page plutôt que de courir après chaque cache.
    queryClient.invalidateQueries();
    if (typeof window !== 'undefined' && window.location) window.location.reload();
  }, [accessibleOrgs, queryClient]);
  // A stale choice (org no longer accessible after re-login) silently resets to "all".
  // Storage + state only: no reload here (the page is already loading after login,
  // a reload would make it load twice).
  useEffect(() => {
    if (activeOrg && accessibleOrgs.length && !accessibleOrgs.includes(activeOrg)) {
      localStorage.removeItem(ACTIVE_ORG_KEY);
      setActiveOrgState(null);
    }
  }, [activeOrg, accessibleOrgs]);

  // Context value - memoized to prevent unnecessary re-renders
  const value = useMemo(() => ({
    // Group / multi-org
    accessibleOrgs,
    activeOrg,
    setActiveOrg,

    // State
    user,
    tenant,
    features,
    hasFeature: (name) => features?.[name] === true, // le backend reste l'autorité (403 FEATURE_NOT_ENABLED)
    isAuthenticated,
    isLoading,
    error,
    isOffboarded,
    isSuperuser: user?.isSuperuser === true || user?.is_superuser === 1 || user?.is_superuser === true,
    isImpersonating, // ← Now uses state instead of localStorage.getItem()

    // Methods
    login,
    logout,
    refreshAuth,
    changePassword,
    impersonate,
    stopImpersonation,

    // Utilities
    getAccessToken,
    authFetch
  }), [
    // Dependencies: only re-create value when these change
    accessibleOrgs,
    activeOrg,
    setActiveOrg,
    user,
    features,
    tenant,
    isAuthenticated,
    isLoading,
    error,
    isOffboarded,
    isImpersonating,
    login,
    logout,
    refreshAuth,
    changePassword,
    impersonate,
    stopImpersonation,
    getAccessToken,
    authFetch
  ]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

/**
 * Hook to use auth context
 */
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
